import { useEffect, useRef } from "react";

// Fires `onFire` when the user shows exit-intent:
//   • Desktop: mouse leaves the top of the viewport (classic exit-intent).
//   • Mobile / any: history back button (popstate) after we push a sentinel state.
// Only arms when `enabled` becomes true; only fires once per mount (guarded).
export function useExitIntent(onFire, enabled) {
  const firedRef = useRef(false);
  const cbRef = useRef(onFire);
  cbRef.current = onFire;

  useEffect(() => {
    if (!enabled) return undefined;

    const fire = () => {
      if (firedRef.current) return;
      firedRef.current = true;
      try {
        cbRef.current?.();
      } catch (_) {
        /* consumer error should not crash the app */
      }
    };

    // 1. Desktop pointer-out-top exit-intent.
    const onMouseOut = (e) => {
      if (e.relatedTarget || e.toElement) return; // still inside a child
      if (e.clientY <= 0) fire();
    };

    // 2. History back-button exit — push a sentinel entry so popstate fires
    //    before the user actually navigates away.
    const stateMarker = { __waaExitIntent: true, t: Date.now() };
    try {
      window.history.pushState(stateMarker, "");
    } catch (_) {
      /* SSR / restricted context — skip */
    }
    const onPopState = () => fire();

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("popstate", onPopState);
    return () => {
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("popstate", onPopState);
    };
  }, [enabled]);
}
